// import { useModal } from "@/hooks/useModal";
import { useState, useEffect, useCallback } from "react";
import { PurchaseStateTag } from "./PurchaseModalComponents/PurchaseStateTag";
import { PurchaseContent } from "./PurchaseModalComponents/PurchaseContent";
import { BottomTag } from "../BottomTag";
import { callFirebaseFunction } from "@/bridge/callFirebaseFunction";
import type { GameThemeId, StartPurchaseArgs, StartPurchaseRet } from "@skillscratch/shared-types";
import type { UserGamePack } from "@skillscratch/shared-types";
import { gameThemes } from "@/themes/themes";
import { useFirestoreDoc } from "@/hooks/useFirestoreDoc";
import type { UserPurchaseStatus } from "@skillscratch/shared-types";
import { getAuth } from "firebase/auth";
import type { getMostRecentGamePackArgs } from "@/helpers/handleHeadlessPurchase";
import { getStoreById } from "@/helpers/storeListings";
import { centsToPrettyDollars } from "@skillscratch/shared-util";
import { useStartGame } from "@/hooks/useStartGame";
import type { GameActionStartRet } from "@skillscratch/shared-types";
// import { handleHeadlessPurchase } from "@/helpers/handleHeadlessPurchase";


export type PurchaseState = 'options' | 'checkout' | 'processing' | 'success' | 'checkout-failed';

export type Price = {
    id: string;
    totalCards: number;
    price: number;
}

type PurchaseModalProps = {
    id: GameThemeId,
    prices: Price[],
    type?: "refill" | "purchase",
    priceId?: string | null,
}

export type StartGameProps = {
    startGameRet: GameActionStartRet;
    unplayedCard: UserGamePack;
}

export type ConfirmPaymentArgs = {
    storeListingId: string;
    priceId: string;
    purchaseId: string | undefined;
  }


export function PurchaseModal({ id, prices, type = 'purchase', priceId = null }: PurchaseModalProps) {
    // const { closeModal } = useModal();
    const [purchaseState, setPurchaseState] = useState<PurchaseState>('options');
    const [result, setResult] = useState<StartPurchaseRet | undefined>(undefined);
    // const [startGame, setStartGame] = useState<(() => void) | undefined>(undefined);
    const [startGameProps, setStartGameProps] = useState<StartGameProps | undefined>(undefined);
    const [boughtUserGamePack, setBoughtUserGamePack] = useState<UserGamePack | undefined>(undefined);
    const [priceIdProp, setPriceIdProp] = useState<string | undefined>(undefined);
    // const { navigateNormal } = useViewTransition();


    const { startGameHandler } = useStartGame({
        onSuccess: (startGameRet: GameActionStartRet, unplayedCard: UserGamePack) => {
            // const startGameRef = () => {
            //     navigateNormal("/game", {
            //         replace: false,
            //         viewTransition: true,
            //         state: {
            //             gamePackId: unplayedCard.id,
            //             gameThemeId: unplayedCard.gameThemeId,
            //             cardCount: unplayedCard.count,
            //             startGameRet,
            //         },
            //     })
            // }

            setStartGameProps({ 
                startGameRet,
                unplayedCard,
             });

            setPurchaseState("success");

            // setStartGame(() => {
            //     navigateNormal("/game", {
            //         replace: false,
            //         viewTransition: true,
            //         state: {
            //             gamePackId: unplayedCard.id,
            //             gameThemeId: unplayedCard.gameThemeId,
            //             cardCount: unplayedCard.count,
            //             startGameRet,
            //         },
            //     })
            // });
        },
        onError: () => {
            
        }
    });

    
    const auth = getAuth();
    
    const [purchaseStatus, , ,] =
        useFirestoreDoc<UserPurchaseStatus>(
            () => {
            const userId = auth.currentUser?.uid;
            const purchaseId = result?.purchaseId;
            if (!userId || !purchaseId) {
                return;
            }
            return ["user", userId, "purchaseStatus", purchaseId];
            },
            [result?.purchaseId, auth.currentUser?.uid],
            "onSnapshot"
    );

    const fetchMostRecentGamePack = useCallback(async (storeListingId: string, handleDismiss: (success: UserGamePack) => void) => {
        try {
            const boughtUserGamePack = await getMostRecentGamePack(storeListingId);
            handleDismiss(boughtUserGamePack);
        } catch {
            // no-op. Should maybe record in posthog/sentry
        }
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const handleDismiss = useCallback((boughtUserGamePack: UserGamePack) => {
        startGameHandler(boughtUserGamePack);
        return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (purchaseStatus?.status === "completed") {
            // setIsPurchasePending(false);
            fetchMostRecentGamePack(id, handleDismiss);
        }

        if (purchaseStatus?.status === "stripe-pending") {
            setPurchaseState('processing');
        }

        if (purchaseStatus?.status === 'failed') {
            setPurchaseState('checkout-failed');
        }
    }, [purchaseStatus?.status, fetchMostRecentGamePack, id, handleDismiss]);

    useEffect(() => {
        if (!boughtUserGamePack) {
            return;
        }
    }, [boughtUserGamePack]);

    // Handle purchase initiated from PurchaseOptions
    const handlePurchase = async (priceId: string) => {
        setPurchaseState("processing");
        setPriceIdProp(priceId);


        callFirebaseFunction<StartPurchaseArgs, StartPurchaseRet>(
            "startPurchase",
            {
              storeListingId: id,
              priceId,
            },
          ).then((fetchResult) => {
            setResult(fetchResult);

            if (fetchResult.type === 'balance') {
                setBoughtUserGamePack(fetchResult.boughtUserGamePack);
                return;
            }
            
            setTimeout(async () => {
                try {
                    await callFirebaseFunction<ConfirmPaymentArgs, void>("handleStripeHeadless",
                        {
                            storeListingId: id,
                            priceId,
                            purchaseId: fetchResult?.purchaseId,
                        }
                    )
                } catch {
                    // setPurchaseState("checkout-failed");
                }
            }, 0);
            // setPurchaseState("success");
        });
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (!priceId || purchaseState === 'processing') return;
    
        const timeout = setTimeout(() => {
            handlePurchase(priceId);
        }, 1000);
    
        return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // const handleClose = () => {
    //     closeModal();
    // };

    const { colorScheme, card } = gameThemes[id as keyof typeof gameThemes];
    const color = colorScheme.tileColors[1].background;
    const listing = getStoreById(id);
    
  
    return (
        <div className="bg-white rounded-xl shadow-lg pt-8 pb-7 w-80">
            <div className="flex items-center mb-5 px-4">
                <div className="mr-2.5">
                    <img src={card} alt="24K Fortune" className="h-16 rounded-lg" />
                </div>
                <div>
                    <div className="text-black opacity-40 font-semibold">
                        {type === 'purchase' ? 'Win up to' : 'Keep scratching'}
                    </div>
                    <div className="text-4xl font-bold text-blue-dark" style={{ color }}>
                        {centsToPrettyDollars(listing?.prizeAmount ?? 0, 'full2')}
                    </div>
                </div>
            </div>
            <PurchaseStateTag purchaseState={purchaseState} id={id} result={result} />
            <div className='mt-3'>
                <PurchaseContent 
                    purchaseState={purchaseState}
                    handlePurchase={handlePurchase}
                    id={id}
                    prices={prices}
                    result={result}
                    startGameProps={startGameProps}
                    priceIdProp={priceIdProp}
                />
            </div>
            <BottomTag purchaseState={purchaseState} />
        </div>
    );
};

const getMostRecentGamePack = async (storeListingId: string) => {
  // eslint-disable-next-line no-useless-catch
  try {
    return await callFirebaseFunction<getMostRecentGamePackArgs, UserGamePack>(
      "getMostRecentGamePack", {
      storeListingId,
    });
  } catch (error) {
    // biome-ignore lint/complexity/noUselessCatch: <explanation>
    throw error;
  }
};